// The change history over the catalogue and the configurator. It doubles as
// the audit log, so it names technical things technically: an entity, an
// entry, a field. What a human does there — undo, confirm — is German.
import type { ChangeAction, ChangeActor, ChangeEntity } from '../../enums'

import { CALCULATION } from './calculation'
import { CATALOGUE } from './catalogue'
import { CONTROLS } from './controls'
import { PRODUCT_PROPERTY } from './product-property'
import { TERMS } from './terms'

export const HISTORY = {
  subtitle:
    'Jede Änderung an Katalog und Konfigurator, mit Vorher/Nachher und einzeln rückgängig zu machen.',
  empty: 'Noch keine Änderungen aufgezeichnet',

  filterAll: CONTROLS.filter.all,
  filterEntity: 'Bereich',
  filterActor: 'Ausgelöst von',

  bundle: 'AI-Instruktion',
  bundleCount: (n: number) =>
    n === 1
      ? '1 Änderung in dieser Instruktion'
      : `${n} Änderungen in dieser Instruktion`,
  revertBundleAnyway: 'Ganze Instruktion trotzdem rückgängig machen',
  bundleConflictCount: (n: number) =>
    n === 1
      ? '1 Feld wurde seitdem anders gesetzt:'
      : `${n} Felder wurden seitdem anders gesetzt:`,
  revertBundle: 'Ganze Instruktion rückgängig machen',

  revert: 'Rückgängig',
  reverted: 'Rückgängig gemacht',
  revertConfirmTitle: 'Änderung rückgängig machen',
  revertConfirmMessage:
    'Der Stand von vor dieser Änderung wird wiederhergestellt. Das Rückgängigmachen wird selbst als Änderung aufgezeichnet.',
  revertRestoresCascaded: (n: number) =>
    n === 1
      ? 'Ein abhängiger Eintrag, den das Löschen mitgenommen hat, kommt mit zurück.'
      : `${n} abhängige Einträge, die das Löschen mitgenommen hat, kommen mit zurück.`,
  revertAnyway: 'Trotzdem rückgängig machen',

  conflicts: 'Konflikte',
  conflictField: 'Feld',
  conflictsHint:
    'Diese Felder wurden seit dieser Änderung von jemand anderem angepasst. Rückgängig machen überschreibt den aktuellen Stand.',
  conflictAtTime: 'Damals gesetzt',
  conflictNow: 'Aktuell',
  conflictWouldBe: 'Würde werden',

  noValue: '—',

  actionLabels: {
    CREATE: 'Angelegt',
    UPDATE: 'Geändert',
    DELETE: 'Gelöscht',
  } as const satisfies Record<ChangeAction, string>,

  actorLabels: {
    USER: 'Person',
    AI: 'AI',
    SYSTEM: 'System',
  } as const satisfies Record<ChangeActor, string>,

  entityLabels: {
    MEASURE: TERMS.measure,
    MEASURE_PROPERTY: `${TERMS.measure}-${TERMS.characteristic}`,
    MEASURE_PRODUCT: `${TERMS.measure} ↔ ${TERMS.product}`,
    MEASURE_IMAGE: `${TERMS.measure}-Bild`,
    MEASURE_IMAGE_RULE: CATALOGUE.imageRules.title,
    MEASURE_IMAGE_RULE_IMAGE: `Bild in „${CATALOGUE.imageRules.title}“`,
    PRODUCT: TERMS.product,
    PRODUCT_IMAGE: `${TERMS.product}-Bild`,
    LABOUR_ITEM: TERMS.labourItem,
    FORMULA: TERMS.formula,
    CALCULATION_STAGE: TERMS.stage,
    CALCULATION_SLOT: `${TERMS.stage}-Zeile`,
    CALCULATION_SLOT_VARIANT: `${TERMS.stage}-Zeile ↔ Anforderungs-Variante`,
    CALCULATION_BLOCK: TERMS.block,
    CALCULATION_GROUP: TERMS.priceGroup,
    CALCULATION_GROUP_MEMBER: `${TERMS.member} einer ${TERMS.priceGroup}`,
    CALCULATION_TEMPLATE: CALCULATION.stageTemplates,
    CATALOGUE_FOLDER: 'Ordner',
    CONFIGURATOR_GROUP: TERMS.group,
    CONFIGURATOR_QUESTION: TERMS.question,
    CONFIGURATOR_GROUP_QUESTION: `${TERMS.question}-Zuordnung`,
    MEASURE_PLACEMENT: 'Platzierung',
    PRODUCT_CLASSIFICATION: 'Produktklassifikation',
    PRODUCT_PROPERTY_DEFINITION: PRODUCT_PROPERTY.singular,
    PRODUCT_PROPERTY_OPTION: `${PRODUCT_PROPERTY.singular}-Auswahl`,
    PRODUCT_GROUP_PROPERTY: `${TERMS.productGroup}-${TERMS.characteristic}`,
    PRODUCT_PROPERTY_VALUE: `Produkt-${PRODUCT_PROPERTY.singular}`,
    MEASURE_PROPERTY_SPEC: `Maßnahme-${PRODUCT_PROPERTY.singular}`,
    MEASURE_REQUIREMENT_VARIANT: 'Anforderungs-Variante',
    MEASURE_VARIANT_CONDITION: 'Anforderungs-Bedingung',
    MEASURE_PRODUCT_GROUP: `Maßnahme ↔ ${TERMS.productGroup}`,
    MEDIA_ASSET: 'Bild',
    // Not a table: one entry standing for a whole catalogue replacement.
    CONFIGURATOR_RESTORE: 'Konfigurator-Sync',
  } as const satisfies Record<ChangeEntity, string>,

  errors: {
    bundleNotReverted: (n: number) =>
      n === 1
        ? 'Die Änderung dieser Instruktion ließ sich nicht rückgängig machen.'
        : `Keine der ${n} Änderungen dieser Instruktion ließ sich rückgängig machen.`,
    revertConflict: (count: number) =>
      count === 1
        ? 'Ein Feld wurde seitdem geändert. Bestätige, um es trotzdem zu überschreiben.'
        : `${count} Felder wurden seitdem geändert. Bestätige, um sie trotzdem zu überschreiben.`,
    bundleNotFound:
      'Zu dieser Instruktion sind keine Änderungen aufgezeichnet.',
    revertBlocked: (reason: string) =>
      REVERT_BLOCKED_REASONS[reason] ??
      'Diese Änderung lässt sich nicht rückgängig machen.',
  },
} as const

/** Who a change is attributed to. An entry with no actor is the system's, and
 *  every change view names it the same way. */
export const actorOf = (
  actorName: string | null,
  actorEmail: string | null,
): string => actorName ?? actorEmail ?? HISTORY.actorLabels.SYSTEM

/** Why a revert is impossible rather than merely contested. The backend sends
 *  the reason as a code; an unknown one falls back to the general sentence. */
const REVERT_BLOCKED_REASONS: Record<string, string> = {
  ALREADY_REVERTED: 'Diese Änderung wurde bereits rückgängig gemacht.',
  ROW_EXISTS:
    'An dieser Stelle existiert wieder ein Eintrag. Lösche ihn zuerst, dann lässt sich der alte wiederherstellen.',
  ROW_MISSING:
    'Der Eintrag wurde inzwischen gelöscht. Stelle ihn zuerst wieder her.',
  CASCADED_CHILD:
    'Dieser Eintrag ist mit einem anderen zusammen gelöscht worden. Mach dessen Löschung rückgängig — dieser kommt dann mit zurück.',
  ENTITY_UNKNOWN: 'Diese Art von Eintrag gibt es in der Anwendung nicht mehr.',
  NOT_REVERTIBLE:
    'Dieser Eintrag lässt sich nicht einzeln rückgängig machen. Er hält fest, was passiert ist — die Wiederherstellung eines Konfigurators wird durch eine erneute Wiederherstellung rückgängig gemacht.',
}
